import _ from 'lodash'
import $ from 'cash-dom'
import {fmt, s, greyscale, palette, rewind, ffwd, cancel, offset, sfc32, quadMinMax, bezierMinMax} from './util.js'

export default class Plane{
  constructor(book){
    this.book = book
    this.layout = Plane.layout(book)
  }

  attach(dom){
    this.dom = $(dom).find('.diagram svg')
    return this
  }

  static layout(book){
    let {illustrations, pages, stats} = book

    let PG_W = 6,
        FLIP_H = 40

    let {paths, choices, endings} = book,
        {counts, maxCount, depths} = book.stats.choices,
        pageDepth = pg => book.stats.pages.depths[pg],
        fwd = [],
        rev = [],
        ends = [],
        loops = [],
        runs = {}

    _.each(depths, (d, pair) => {
      let [at, to] = _.split(pair, ',', 2).map(_.toInteger),
          src = {x: (at+1)*PG_W + d*FLIP_H, y:d*FLIP_H},
          dst = {x: (to+1)*PG_W + d*FLIP_H, y:d*FLIP_H},
          rad = Math.abs(at-to) * PG_W / 2,
          rot = at > to ? 180 : 0,
          count = counts[pair];
      (at > to ? rev : fwd).push({src, dst, rad, rot, d, weight:count/maxCount})
      if (pageDepth(to) < d){
        // BUG: off-by-one
        let loopD = pageDepth(to),
            loop = {x: (to+1)*PG_W + loopD*FLIP_H, y:loopD*FLIP_H}
        loops.push({d, src:dst, dst:loop})
      }
      if (endings.includes(to)){
        ends.push(_.assign({d}, dst))
      }
    })

    fwd.sort((a,b) => a.d - b.d || a.weight - b.weight)
    rev.sort((a,b) => a.d - b.d || a.weight - b.weight)

    let width = (pages+2) * PG_W,
        height = stats.depth * FLIP_H,
        minY = Math.min(0, _.min(fwd.map(({src, rad}) => src.y - rad))) - 6,
        maxY = Math.max(stats.depth * FLIP_H, _.max(rev.map(({src, rad}) => src.y + rad))) + 6

    return {width, height, fwd, rev, ends, loops, step:FLIP_H, minY, maxY}
  }

  setDepth(depth){
    let {choices, endings, depth:maxDepth} = this.book.stats,
        pct = depth % 1,
        inv = 1 - pct,
        over = [],
        under = []

    let {fwd, rev, ends, step} = this.layout

    if (pct==0 && depth<maxDepth){
      // nothing to be done
    }else if (pct < .2) {
      let toDepth = Math.ceil(depth)
      fwd.filter(({d}) => d==toDepth).forEach( ({src,dst,rad,weight}) => {
        over.push(
          `<path d="M ${src.x-step},${src.y-step} l ${step*pct/.2},${step*pct/.2}" fill="none" stroke="${palette(1-weight)}" stroke-width="3"/>`
        )
      })

      rev.filter(({d}) => d==toDepth).forEach( ({src,dst,rad,weight}) => {
        under.push(
          `<path d="M ${src.x-step},${src.y-step} l ${step*pct/.2},${step*pct/.2}" fill="none" stroke="${palette(1-weight)}" stroke-width="3"/>`
        )
      })
    }else if (pct){
      let toDepth = Math.ceil(depth),
          theta = (pct-.2)/.8 * Math.PI

      fwd.filter(({d}) => d==toDepth).forEach( ({src,dst,rad,weight}) => {
        let end = {x:src.x + rad + rad*Math.cos(Math.PI + theta), y:src.y + rad*Math.sin(Math.PI + theta)}
        over.push(
          `<path d="M ${src.x-step},${src.y-step} L ${src.x},${src.y} A ${rad} ${rad} 0 0 1 ${end.x} ${end.y}" fill="none" stroke="${palette(1-weight)}" stroke-width="3"/>`
        )
      })

      rev.filter(({d}) => d==toDepth).forEach( ({src,dst,rad,weight}) => {
        let end = {x:src.x - rad + rad*Math.cos(theta), y:src.y + rad*Math.sin(theta)}
        under.push(
          `<path d="M ${src.x-step},${src.y-step} L ${src.x},${src.y} A ${rad} ${rad} 0 0 1 ${end.x} ${end.y}" fill="none" stroke="${palette(1-weight)}" stroke-width="3"/>`
        )
      })

    }

    this.dom.find('.over').html(over.join(' '))
    this.dom.find('.under').html(under.join(' '))
  }

  get svg(){
    let {width, height, fwd, rev, ends, loops, step, minY, maxY} = this.layout

    const toArc = ({src, dst, rad, rot, weight, d}) => (
      `<path class="page depth-${d}" d="M ${src.x-step},${src.y-step} L ${src.x},${src.y} A ${rad} ${rad} 0 0 1 ${dst.x},${dst.y}" fill="none" stroke="${palette(1-weight)}" stroke-width="3"/>`
    )

    const toDot = ({x, y, d}) => (
      `<circle class="ending depth-${d}" fill='skyblue' cx="${x}" cy="${y}" r="3" />`
    )

    const toLoopback = ({d, src, dst}) => (
      `<path class="ending depth-${d}" d="M ${src.x},${src.y} L ${dst.x},${dst.y}" stroke="green" stroke-dasharray="2,6" fill="none" stroke-width="3" />`
    )

    let ground = `<polygon points="0,0 ${width},0 ${width + height},${height} ${height},${height} 0,0" fill="#222c" />`

    return `<svg class="matrix" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width+height} ${maxY-minY}">
      <g transform="translate(0,${-minY})">
        ${rev.map(toArc).join('')}
        <g class="under"></g>
        ${ground}
        ${fwd.map(toArc).join('')}
        <g class="over"></g>
        ${loops.map(toLoopback).join('')}
        ${ends.map(toDot).join('')}
      </g>
    </svg>`
  }
}

